import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// Remembers scroll position per route, and restores it correctly:
// - PUSH (going forward to a new screen) -> scroll to top, like opening a fresh page
// - POP (going back) -> restore the exact scroll position you were at before
//
// This mimics native app / browser behavior, where going back returns you
// to where you left off, but going forward always starts fresh.
const scrollPositions = new Map();

function ScrollToTop() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    // Save the CURRENT scroll position under the PREVIOUS path,
    // right before we navigate away from it.
    return () => {
      scrollPositions.set(prevPathRef.current, window.scrollY);
    };
  }, [location.pathname]);

  useEffect(() => {
    prevPathRef.current = location.pathname;

    if (navigationType === 'POP') {
      // Going back — restore the saved position, if we have one.
      const savedY = scrollPositions.get(location.pathname) || 0;
      // Small timeout ensures this runs AFTER the new screen's content has rendered,
      // otherwise scrollTo can fire before there's enough content height to scroll to.
      setTimeout(() => window.scrollTo(0, savedY), 0);
    } else {
      // PUSH or REPLACE — new screen, start at the top.
      window.scrollTo(0, 0);
    }
  }, [location.pathname, navigationType]);

  return null;
}

export default ScrollToTop;